import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper
} from '@mui/material';
import axios from 'axios';
import buildingImage from './../../../Images/house.jpeg';
import CustomBottomNav from './CustomNav';
import { BASE_URL } from './../../Api/ApiUrls';

const Buy_Property_Map = () => {
  const [properties, setProperties] = useState([]);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const getMarkerColor = (createdAt) => {
    if (!createdAt) return '#808080';
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - createdDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = diffDays / 30.44;

    if (diffMonths <= 3) return '#008000';
    if (diffMonths <= 6) return '#FFFF00';
    if (diffMonths <= 12) return '#FFA500';
    return '#FF0000';
  };

  useEffect(() => {
    const loadOlaMaps = async () => {
      if (!window.OlaMaps) {
        const script = document.createElement('script');
        script.src = 'https://api.olamaps.com/v2/olamaps.js';
        script.async = true;
        script.onload = initializeMap;
        script.onerror = () => console.error('Failed to load Ola Maps script');
        document.body.appendChild(script);
      } else {
        initializeMap();
      }
    };

    const initializeMap = () => {
      if (!mapContainerRef.current || !window.OlaMaps) return;

      try {
        const olaMaps = new window.OlaMaps({
          apiKey: 'Icq3iPdYarCdv3I9kEmWB07guY8EYD4R6nAB8CUu'
        });

        const myMap = olaMaps.init({
          container: mapContainerRef.current,
          style: 'https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json',
          center: [78.4844811, 17.5366218],
          zoom: 12
        });

        setMap(myMap);
        myMap.on('load', () => setMapLoaded(true));
        myMap.on('error', (e) => console.error('Map error:', e.error));
      } catch (error) {
        console.error('Error initializing Ola Maps:', error);
      }
    };

    loadOlaMaps();
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const categoriesResponse = await axios.get(`${BASE_URL}/property-category/`);
        const categories = categoriesResponse.data;

        const propertiesResponse = await axios.get(`${BASE_URL}/property/`);
        const filtered = propertiesResponse.data.filter(item =>
          item.type && item.type.toLowerCase().includes("sell")
        );

        const parsed = filtered.map(item => {
          const parseCoord = (coord) => {
            if (!coord) return null;
            const cleaned = coord.replace(/"/g, '').replace(':', '.');
            return parseFloat(cleaned);
          };

          const matchedCategory = categories.find(
            cat => cat.category_id == item.category_id
          );

          const categoryName = matchedCategory ? matchedCategory.category : 'Property';
          const images = item.property_images?.length > 0
            ? item.property_images.map(img => `${BASE_URL}${img.image}`)
            : [buildingImage];

          return {
            id: item.property_id,
            title: categoryName,
            location: item.location || 'Not specified',
            price: `₹${item.price}`,
            date: item.created_at?.split('T')[0],
            facing: item.facing?.replace(/"/g, '') || 'N/A',
            area: item.site_area ? `${item.site_area} sq ft` : 'N/A',
            dimensions: item.roadwidth ? `${item.roadwidth} ft road` : 'N/A',
            listedBy: item.posted_by?.replace(/"/g, '') || 'Agent',
            lat: parseCoord(item.lat),
            lng: parseCoord(item.long),
            length: item.length,
            width: item.width,
            property_name: item.property_name,
            mobile_no: item.mobile_no,
            images: images,
            propertyData: item,
            createdAt: item.created_at
          };
        });

        setProperties(parsed);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !map || properties.length === 0) return;

    markers.forEach(marker => {
      try {
        if (map.getLayer(marker.id)) map.removeLayer(marker.id);
        if (map.getSource(marker.id)) map.removeSource(marker.id);
      } catch (error) {
        console.error('Error removing marker:', error);
      }
    });

    const getPriceLabel = (price) => {
      if (price >= 10000000) return `${Math.round(price / 10000000)} Cr`;
      if (price >= 100000) return `${Math.round(price / 100000)} L`;
      if (price >= 1000) return `${Math.round(price / 1000)} K`;
      return `${price}`;
    };

    const newMarkers = properties
      .filter(property => property.lat && property.lng)
      .map(property => {
        const markerId = `marker-${property.id}`;
        const markerColor = getMarkerColor(property.createdAt);
        const price = parseInt(property.price.replace(/[^\d]/g, ''));
        const label = getPriceLabel(price);

        try {
          map.addSource(markerId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [property.lng, property.lat]
              },
              properties: {
                id: property.id,
                title: property.title,
                price: property.price
              }
            }
          });

          // Inside your useEffect that updates markers

          map.addLayer({
            id: markerId,
            type: 'circle',
            source: markerId,
            paint: {
              'circle-radius': [
                'interpolate', ['linear'], ['zoom'],
                10, 6,     // Smaller at zoom level 10
                12, 10,    // Moderate at zoom level 12
                16, 24     // Larger at zoom level 16
              ],
              'circle-color': markerColor,
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff'
            }
          });

         


          map.on('click', markerId, (e) => {
            const feature = e.features[0];
            if (feature) {
              const propertyId = feature.properties.id;
              const clickedProperty = properties.find(p => p.id === propertyId);
              if (clickedProperty) {
                alert(`Clicked on property: ${clickedProperty.title}\nLocation: ${clickedProperty.location}\nListed: ${clickedProperty.date}`);
              }
            }
          });

          map.on('mouseenter', markerId, () => {
            map.getCanvas().style.cursor = 'pointer';
          });

          map.on('mouseleave', markerId, () => {
            map.getCanvas().style.cursor = '';
          });

          return { id: markerId, propertyId: property.id };
        } catch (error) {
          console.error('Error adding marker for property', property.id, error);
          return null;
        }
      }).filter(Boolean);

    setMarkers(newMarkers);

    const coordinates = properties
      .filter(p => p.lat && p.lng)
      .map(p => [p.lng, p.lat]);

    if (coordinates.length > 0) {
      const minLng = Math.min(...coordinates.map(c => c[0]));
      const maxLng = Math.max(...coordinates.map(c => c[0]));
      const minLat = Math.min(...coordinates.map(c => c[1]));
      const maxLat = Math.max(...coordinates.map(c => c[1]));

      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: 50,
        maxZoom: 15
      });
    }
  }, [map, mapLoaded, properties]);

  return (
    <Box sx={{ pb: 7, maxWidth: 480, mx: "auto", position: 'relative', backgroundColor: 'rgb(239, 231, 221)', minHeight: '100vh' }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
          Properties for Sale
        </Typography>
      </Box>
      <Box sx={{ px: 2, pb: 10 }}>
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: 'calc(100vh - 150px)',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#f0f0f0'
          }}
        >
          {!mapLoaded && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>Loading map...</Typography>
            </Box>
          )}
        </div>
      </Box>
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <CustomBottomNav />
      </Paper>
    </Box>
  );
};

export default Buy_Property_Map;
